/* ============================================================
   ATR CUSTOM BEHAVIOR — ported from the approved static wireframe.
   Content-generation (marquee/runs/trails/conditions/events/gallery)
   was moved to Liquid {% for %} loops in the section files per the
   handoff's rendering-strategy requirement — this file keeps only
   genuine behavior: next-run countdown, headlamp note, scroll HUD,
   reveal-on-scroll, nav burger, and the trail-map/headlamp layers.
   ============================================================ */
const $ = s => document.querySelector(s);

/* ============================================================
   NEXT RUN COUNTDOWN + sunset headlamp note
   Reads run times from data-* attributes rendered by Liquid onto
   #nextRun (see sections/atr-hero.liquid) instead of ATR_DATA.
   Timezone pinned to America/New_York per the handoff (was: visitor's
   local clock, which showed the wrong countdown to out-of-town visitors).
   ============================================================ */
const ATR_TZ = 'America/New_York';

function nowInTZ() {
  // Build a Date whose getHours/getDay/etc. read as America/New_York wall-clock time,
  // regardless of the visitor's own timezone.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ATR_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short'
  }).formatToParts(new Date());
  const get = t => parts.find(p => p.type === t).value;
  const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    date: new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`),
    dow: days[get('weekday')]
  };
}

function fmtTime(h, m) {
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
}

function nextOccurrence(r, now, nowDow) {
  const d = new Date(now);
  d.setHours(r.h, r.m, 0, 0);
  let delta = (r.dow - nowDow + 7) % 7;
  if (delta === 0 && d <= now) delta = 7;
  d.setDate(d.getDate() + delta);
  return d;
}

let _target = null, _run = null, _runs = [];

function readRunsFromDOM() {
  const el = document.getElementById('nextRun');
  if (!el || !el.dataset.runs) return [];
  try { return JSON.parse(el.dataset.runs); } catch (e) { return []; }
}

function computeNext() {
  const { date: now, dow: nowDow } = nowInTZ();
  const n = _runs.map(r => ({ r, at: nextOccurrence(r, now, nowDow) })).sort((a, b) => a.at - b.at)[0];
  if (!n) return;
  _run = n.r; _target = n.at;
}

/* static parts render once — rebuilding them every tick destroyed keyboard focus */
function renderNextRun() {
  if (!_run) return;
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const whenEl = $('#nrWhen'), whereEl = $('#nrWhere');
  if (whenEl) whenEl.textContent = `${days[_run.dow]} ${fmtTime(_run.h, _run.m)} — ${_run.label}`;
  if (whereEl) whereEl.innerHTML = `${_run.where}${_run.confirmUrl ? ` · <a href="${_run.confirmUrl}" target="_blank" rel="noopener">confirm location</a>` : ''}`;

  // Augusta daylight approximation, phased to the June solstice (doy 172).
  // Checks BOTH ends — an early-morning run starts in the dark most of the year.
  const { date: now } = nowInTZ();
  const doy = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 864e5);
  const phase = Math.cos((doy - 172) * 2 * Math.PI / 365);
  const sunriseMin = 415 - 40 * phase;   // ~6:15am midsummer → ~7:35am midwinter
  const sunsetMin = 1142 + 96 * phase;   // ~8:38pm midsummer → ~5:26pm midwinter
  const runMin = _run.h * 60 + _run.m;
  const lamp = $('#lampNote');
  if (!lamp) return;
  if (runMin < sunriseMin + 15)
    lamp.innerHTML = `<span class="lamp">◉ HEADLAMP REQUIRED</span> — you'll start in the dark and watch the sun come up`;
  else if (runMin > sunsetMin - 30)
    lamp.innerHTML = `<span class="lamp">◉ HEADLAMP REQUIRED</span> — this one finishes in the dark`;
  else
    lamp.innerHTML = `<span class="lamp">☀</span> daylight run — sunscreen over headlamp`;
}

function tick() {
  if (!_target) return;
  const { date: now } = nowInTZ();
  const ms = _target - now;
  if (ms <= 0) { computeNext(); renderNextRun(); return; }
  const d = Math.floor(ms / 864e5), hh = Math.floor(ms / 36e5) % 24,
        m = Math.floor(ms / 6e4) % 60, s = Math.floor(ms / 1e3) % 60;
  const el = $('#nrCount');
  if (el) el.textContent = `T-MINUS ${d}D ${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

_runs = readRunsFromDOM();
if (_runs.length) {
  computeNext(); renderNextRun(); tick();
  let _iv = setInterval(tick, 1000);
  document.addEventListener('visibilitychange', () => {
    clearInterval(_iv);
    if (!document.hidden) { tick(); _iv = setInterval(tick, 1000); }
  });
}

/* ============================================================
   SCROLL: HUD, reveals, dividers, count-ups
   ============================================================ */
const sectionNames = [
  ['top', 'TRAILHEAD'], ['runs', 'GROUP RUNS'], ['first', 'FIRST RUN'], ['trails', 'THE TRAILS'],
  ['conditions', 'CONDITIONS'], ['giveback', 'GIVE BACK'], ['events', 'RACE CALENDAR'],
  ['about', 'ABOUT'], ['gallery', 'FIELD NOTES'], ['join', 'FINISH CHUTE']
];
function onScroll() {
  const doc = document.documentElement;
  const max = doc.scrollHeight - innerHeight;
  const p = max > 0 ? Math.min(1, scrollY / max) : 0;
  const fill = $('#hudFill');
  if (fill) fill.style.width = (p * 100).toFixed(2) + '%';
  let current = 'TRAILHEAD';
  for (const [id, name] of sectionNames) {
    const el = document.getElementById(id);
    if (el && el.getBoundingClientRect().top < innerHeight * 0.5) current = name;
  }
  const mile = $('#hudMile');
  if (mile) mile.innerHTML = `MI <b>${(p * 6.2).toFixed(2)}</b> / 6.20 · ${current}`;
}
let _hudTick = false;
addEventListener('scroll', () => {
  if (_hudTick) return;
  _hudTick = true;
  requestAnimationFrame(() => { onScroll(); _hudTick = false; });
}, { passive: true });
onScroll();

if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(en => {
      if (!en.isIntersecting) return;
      en.target.classList.add('on');
      if (en.target.classList.contains('divider')) en.target.classList.add('drawn');
      en.target.querySelectorAll('.count').forEach(c => {
        if (c.dataset.done) return;
        c.dataset.done = 1;
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) { c.textContent = c.dataset.to; return; }
        const to = +c.dataset.to, t0 = performance.now(), dur = 1400;
        (function tick(now) {
          const k = Math.min(1, (now - t0) / dur);
          c.textContent = Math.round(to * (1 - Math.pow(1 - k, 3)));
          if (k < 1) requestAnimationFrame(tick);
        })(t0);
      });
      io.unobserve(en.target);
    });
  }, { threshold: .25 });
  document.querySelectorAll('.reveal,.divider,.stats > div').forEach(el => io.observe(el));
  // failsafe: if anything above threw, don't strand the page at opacity:0
  setTimeout(() => document.querySelectorAll('.reveal:not(.on)').forEach(el => el.classList.add('on')), 3000);
} else {
  document.querySelectorAll('.reveal,.divider').forEach(el => el.classList.add('on', 'drawn'));
}

/* ============================================================
   PHOTO GALLERY — self-remove any image that 404s; hide the whole
   section if every photo fails (kept as a resilience net even
   though photos now come from Shopify Files via image_picker blocks).
   ============================================================ */
document.querySelectorAll('#galleryStrip .photo-card img').forEach(img => {
  img.addEventListener('error', () => {
    const card = img.closest('.photo-card');
    const strip = document.getElementById('galleryStrip');
    if (card) card.remove();
    if (strip && !strip.children.length) {
      const gallery = document.getElementById('gallery');
      if (gallery) gallery.hidden = true;
    }
  });
});

/* ============================================================
   NAV BURGER
   ============================================================ */
const burgerEl = $('#burger'), navLinksEl = $('#navLinks');
if (burgerEl && navLinksEl) {
  function closeNav() {
    navLinksEl.classList.remove('open');
    burgerEl.setAttribute('aria-expanded', 'false');
  }
  burgerEl.addEventListener('click', () => {
    const open = navLinksEl.classList.toggle('open');
    burgerEl.setAttribute('aria-expanded', open);
  });
  navLinksEl.addEventListener('click', e => {
    if (e.target.tagName === 'A') closeNav();
  });
  addEventListener('keydown', e => {
    if (e.key === 'Escape' && navLinksEl.classList.contains('open')) { closeNav(); burgerEl.focus(); }
  });
  document.addEventListener('click', e => {
    if (navLinksEl.classList.contains('open') && !e.target.closest('nav')) closeNav();
  });
}

const yearEl = $('#year');
if (yearEl) yearEl.textContent = new Date().getFullYear();

/* ============================================================
   TRAIL MAP LAYERS + TRAVELLING HEADLAMP
   ============================================================ */
(() => {
  const panels = [...document.querySelectorAll('.hero, .subhero, section, footer')];
  if (!panels.length) return;
  // deterministic crop per panel so scrolling reads as travelling across the map
  // zoom stays inside what the 2600px source can render sharply
  // (>190% on a wide desktop starts upscaling and goes soft)
  const views = [
    [128, 18, 22], [170, 72, 30], [145, 40, 68], [188, 15, 85], [118, 88, 46],
    [175, 55, 12], [135, 30, 40], [190, 78, 74], [124, 62, 90], [158, 8, 58],
    [148, 45, 25], [115, 92, 66], [182, 35, 50], [138, 70, 8]
  ];
  panels.forEach((el, i) => {
    const [zoom, px, py] = views[i % views.length];
    const box = document.createElement('div');
    box.className = 'mapbg';
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML = '<i class="dim"></i><i class="lit"></i>';
    box.querySelectorAll('i').forEach(layer => {
      layer.style.backgroundSize = zoom + '%';
      layer.style.backgroundPosition = px + '% ' + py + '%';
    });
    el.prepend(box);
  });

  const fine = matchMedia('(pointer:fine)').matches;
  if (!fine) { document.body.classList.add('no-beam'); return; }

  // only paint panels that are actually on screen
  const live = new Set();
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      es => es.forEach(e => e.isIntersecting ? live.add(e.target) : (live.delete(e.target),
        e.target.querySelector(':scope > .mapbg')?.classList.remove('awake'))),
      { threshold: 0 });
    panels.forEach(el => io.observe(el));
  } else {
    panels.forEach(el => live.add(el));
  }

  let mx = -9999, my = -9999, queued = false;
  const paint = () => {
    queued = false;
    live.forEach(el => {
      const box = el.querySelector(':scope > .mapbg');
      if (!box) return;
      const r = el.getBoundingClientRect();
      const inside = my >= r.top - 120 && my <= r.bottom + 120;
      box.classList.toggle('awake', inside);
      if (!inside) return;
      box.style.setProperty('--lx', (mx - r.left).toFixed(0) + 'px');
      box.style.setProperty('--ly', (my - r.top).toFixed(0) + 'px');
    });
  };
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(paint); } };
  addEventListener('mousemove', e => { mx = e.clientX; my = e.clientY; queue(); }, { passive: true });
  addEventListener('scroll', queue, { passive: true });   // beam keeps up while scrolling
  addEventListener('mouseleave', () => {
    live.forEach(el => el.querySelector(':scope > .mapbg')?.classList.remove('awake'));
  });
})();
